import type { DbaDiscountRow } from '@vivport/backend/types/dba';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { apiErrorMessage } from '@/api/errors';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { StripeWarning } from '@/components/stripe-warning';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Notice } from '@/components/ui/notice';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRemoveDiscount, useSetDiscount } from '@/hooks/use-dba-mutations';
import { useDiscounts } from '@/hooks/use-dba-queries';
import { formatDateTime } from '@/lib/format';

function isValidPercent(value: string): boolean {
  const percent = Number(value);
  return value.trim() !== '' && Number.isInteger(percent) && percent >= 1 && percent <= 100;
}

function AddDiscountRow({ onError }: { onError: (message: string | null) => void }) {
  const setDiscount = useSetDiscount();
  const [email, setEmail] = useState('');
  const [percent, setPercent] = useState('');
  const [note, setNote] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    onError(null);
    try {
      await setDiscount.mutateAsync({
        email: email.trim().toLowerCase(),
        body: { percentOff: Number(percent), note: note.trim() || undefined },
      });
      setEmail('');
      setPercent('');
      setNote('');
    } catch (err) {
      onError(apiErrorMessage(err));
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
      <Input
        className="w-64"
        type="email"
        placeholder="Email"
        aria-label="Email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        required
      />
      <Input
        className="w-24"
        type="number"
        min={1}
        max={100}
        step={1}
        placeholder="%"
        aria-label="Percent off"
        value={percent}
        onChange={(event) => setPercent(event.target.value)}
        required
      />
      <Input
        className="w-72"
        placeholder="Note"
        aria-label="Note"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      <Button type="submit" disabled={setDiscount.isPending || !isValidPercent(percent)}>
        Add discount
      </Button>
    </form>
  );
}

function DiscountRow({ row, onError }: { row: DbaDiscountRow; onError: (message: string | null) => void }) {
  const setDiscount = useSetDiscount();
  const removeDiscount = useRemoveDiscount();
  const [editing, setEditing] = useState(false);
  const [percent, setPercent] = useState(String(row.percentOff));
  const [note, setNote] = useState(row.note ?? '');

  const startEdit = () => {
    setPercent(String(row.percentOff));
    setNote(row.note ?? '');
    setEditing(true);
  };

  const save = async () => {
    onError(null);
    try {
      await setDiscount.mutateAsync({
        email: row.email,
        body: { percentOff: Number(percent), note: note.trim() || undefined },
      });
      setEditing(false);
    } catch (err) {
      onError(apiErrorMessage(err));
    }
  };

  return (
    <TableRow>
      <TableCell>
        <div>{row.email}</div>
        {!row.discountAppliesToExistingSubscription && <StripeWarning className="mt-2" />}
      </TableCell>
      <TableCell className="text-right">
        {editing ? (
          <Input
            className="ml-auto w-20"
            type="number"
            min={1}
            max={100}
            step={1}
            aria-label="Percent off"
            value={percent}
            onChange={(event) => setPercent(event.target.value)}
          />
        ) : (
          `${row.percentOff}%`
        )}
      </TableCell>
      <TableCell>
        {editing ? (
          <Input aria-label="Note" value={note} onChange={(event) => setNote(event.target.value)} />
        ) : (
          row.note
        )}
      </TableCell>
      <TableCell>
        {row.accountId === null ? (
          <span className="text-muted-foreground">No account</span>
        ) : (
          <Link to={`/accounts/${row.accountId}`} className="underline underline-offset-4">
            Account {row.accountId}
          </Link>
        )}
      </TableCell>
      <TableCell>{row.arrangement && <Badge variant="secondary">{row.arrangement}</Badge>}</TableCell>
      <TableCell className="whitespace-nowrap">{formatDateTime(row.updatedAt)}</TableCell>
      <TableCell>
        <div className="flex justify-end gap-2">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={setDiscount.isPending}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={save}
                disabled={setDiscount.isPending || !isValidPercent(percent)}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={startEdit}>
                Edit
              </Button>
              <ConfirmDialog
                triggerLabel="Remove"
                triggerVariant="ghost"
                title="Remove discount"
                description={`Removes the ${row.percentOff}% discount for ${row.email}. Future checkouts for this email charge the full price.${row.arrangement === 'COMPLIMENTARY' ? ' Complimentary access stays until you end it.' : ''}`}
                confirmLabel="Remove discount"
                confirmVariant="destructive"
                onConfirm={() => removeDiscount.mutateAsync(row.email)}
              >
                {!row.discountAppliesToExistingSubscription && <StripeWarning />}
              </ConfirmDialog>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function DiscountsPage() {
  const discounts = useDiscounts();
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <h1 className="text-xl font-medium">Discounts</h1>
      <Card>
        <CardContent className="space-y-4 pt-6">
          <AddDiscountRow onError={setError} />
          {error && <Notice tone="destructive">{error}</Notice>}
          {discounts.isError && <Notice tone="destructive">{apiErrorMessage(discounts.error)}</Notice>}
          {discounts.isPending && <p className="text-sm text-muted-foreground">Loading discounts…</p>}
          {discounts.data && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Percent</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Arrangement</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {discounts.data.map((row) => (
                  <DiscountRow key={row.email} row={row} onError={setError} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
